'use client';
import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';

const sections = [
  {
    id: 'privacy',
    title: 'Privacy Policy',
    icon: 'ShieldCheckIcon',
    lastUpdated: 'June 2026',
    content: [
      {
        heading: '1. Information We Collect',
        body: 'We collect personal information you provide when registering, booking a round, or contacting us. This includes your name, email address, mobile number, date of birth, SA ID number, address, emergency contact details, golf handicap, and CDH number. We also collect payment information processed securely through PayFast.',
      },
      {
        heading: '2. How We Use Your Information',
        body: 'Your information is used to process bookings and payments, send booking confirmations and receipts, manage your loyalty programme membership, communicate club news and events (if you have opted in), and improve our services. We do not sell your personal information to third parties.',
      },
      {
        heading: '3. Data Storage & Security',
        body: 'Your data is stored securely on Supabase infrastructure with row-level security policies. Payment transactions are processed by PayFast (PCI DSS Level 1 certified) and we do not store card details. All data is encrypted in transit using 256-bit SSL.',
      },
      {
        heading: '4. Your Rights (POPIA)',
        body: 'Under the Protection of Personal Information Act (POPIA), you have the right to access, correct, or request deletion of your personal information. To exercise these rights, contact us at golf@maritzburggolfclub.co.za or call 033 396 2356.',
      },
      {
        heading: '5. Retention',
        body: 'We retain your booking and transaction records for a minimum of 5 years for accounting and legal compliance purposes. Marketing preferences can be updated at any time through your member portal.',
      },
      {
        heading: '6. Contact',
        body: 'For privacy-related queries, contact the Club Manager at Maritzburg Golf Club, New England Road, Pietermaritzburg, 3201, or email golf@maritzburggolfclub.co.za.',
      },
    ],
  },
  {
    id: 'terms',
    title: 'Terms of Use',
    icon: 'DocumentTextIcon',
    lastUpdated: 'June 2026',
    content: [
      {
        heading: '1. Acceptance',
        body: 'By using the Maritzburg Golf Club online booking platform, you agree to these Terms of Use. If you do not agree, please do not use the platform.',
      },
      {
        heading: '2. Booking & Payment',
        body: 'All bookings are subject to availability and confirmation. Payment is processed securely via PayFast. Your booking is confirmed only once payment is successfully completed and you receive a booking confirmation email.',
      },
      {
        heading: '3. Cancellation Policy',
        body: 'Cancellations made more than 24 hours before the tee time are eligible for a full refund. Cancellations within 24 hours of the tee time are non-refundable. Refunds are processed within 5–7 business days via the original payment method.',
      },
      {
        heading: '4. Course Rules',
        body: 'All players must adhere to the Maritzburg Golf Club course rules, dress code, and etiquette standards. The club reserves the right to refuse access to any player who does not comply with club rules.',
      },
      {
        heading: '5. Indemnity',
        body: 'Players participate at their own risk. Maritzburg Golf Club accepts no liability for personal injury, loss, or damage to property during play, except where caused by the gross negligence of the club.',
      },
      {
        heading: '6. Intellectual Property',
        body: 'All content on this platform, including logos, images, and text, is the property of Maritzburg Golf Club and may not be reproduced without written permission.',
      },
      {
        heading: '7. Governing Law',
        body: 'These terms are governed by the laws of the Republic of South Africa. Any disputes shall be subject to the jurisdiction of the courts of KwaZulu-Natal.',
      },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookie Policy',
    icon: 'CpuChipIcon',
    lastUpdated: 'June 2026',
    content: [
      {
        heading: '1. What Are Cookies',
        body: 'Cookies are small text files stored on your device when you visit our platform. They help us provide a better experience by remembering your preferences and keeping you signed in.',
      },
      {
        heading: '2. Cookies We Use',
        body: 'We use essential session cookies to maintain your login state (Supabase authentication tokens). These are strictly necessary for the platform to function and cannot be disabled. We do not use advertising or tracking cookies.',
      },
      {
        heading: '3. Third-Party Cookies',
        body: 'PayFast may set cookies during the payment process for fraud prevention and security purposes. These are governed by PayFast\'s own privacy policy.',
      },
      {
        heading: '4. Managing Cookies',
        body: 'You can control cookies through your browser settings. Disabling essential cookies will prevent you from logging in and using the booking platform. For more information, visit your browser\'s help documentation.',
      },
      {
        heading: '5. Contact',
        body: 'For questions about our use of cookies, contact us at golf@maritzburggolfclub.co.za.',
      },
    ],
  },
];

export default function LegalPage() {
  const [activeSection, setActiveSection] = useState('privacy');

  const current = sections.find(s => s.id === activeSection) || sections[0];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicNav />

      {/* Hero */}
      <div className="bg-primary text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="ScaleIcon" size={28} variant="outline" className="text-accent" />
          </div>
          <h1 className="text-3xl font-extrabold mb-2">Legal & Policies</h1>
          <p className="text-white/70 text-sm max-w-lg mx-auto">
            Maritzburg Golf Club is committed to transparency. Review our policies below.
          </p>
        </div>
      </div>

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        {/* Tab navigation */}
        <div className="flex gap-2 mb-8 bg-muted/60 rounded-2xl p-1 overflow-x-auto scrollbar-hide">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 whitespace-nowrap flex-1 justify-center ${
                activeSection === s.id
                  ? 'bg-card text-foreground shadow-card'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={s.icon as any} size={15} variant={activeSection === s.id ? 'solid' : 'outline'} />
              {s.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden fade-in" key={current.id}>
          <div className="gradient-primary px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Icon name={current.icon as any} size={20} variant="outline" className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">{current.title}</h2>
                <p className="text-white/60 text-xs">Last updated: {current.lastUpdated}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {current.content.map((section, idx) => (
              <div key={idx} className="border-b border-border last:border-0 pb-6 last:pb-0">
                <h3 className="font-bold text-foreground mb-2 text-sm">{section.heading}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{section.body}</p>
              </div>
            ))}
          </div>

          <div className="px-6 py-4 bg-muted/30 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Questions about this policy? Contact us at{' '}
              <a href="mailto:golf@maritzburggolfclub.co.za" className="text-primary hover:text-secondary font-medium transition-colors">
                golf@maritzburggolfclub.co.za
              </a>{' '}
              or call{' '}
              <a href="tel:0333962356" className="text-primary hover:text-secondary font-medium transition-colors">
                033 396 2356
              </a>.
            </p>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
